import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../api/auth';
import { getErrorMessage } from '../api/client';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Alert from '../components/ui/Alert';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyEmail } = useAuth();

  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (cooldown > 0) {
      const t = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [cooldown]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyEmail(email, code);
      navigate('/dashboard');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setSuccess('');
    setResending(true);
    try {
      const res = await authApi.resendCode({ email });
      setSuccess(res.message || 'Código reenviado. Revisa tu correo.');
      setCooldown(60);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 font-extrabold text-lg justify-center mb-8">
          <span className="w-9 h-9 rounded-lg bg-brand-500 text-white grid place-items-center">GF</span>
          Go Fiesta
        </Link>
        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <h1 className="text-2xl font-extrabold mb-1">Verifica tu correo</h1>
          <p className="text-sm text-slate-500 mb-6">
            Te enviamos un código de 6 dígitos a <b>{email}</b>.
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            {error && <Alert type="error">{error}</Alert>}
            {success && <Alert type="success">{success}</Alert>}

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <Input
              label="Código"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className="text-center tracking-[0.5em] text-2xl font-bold"
              required
            />

            <Button type="submit" disabled={loading || code.length !== 6} className="w-full">
              {loading ? 'Verificando...' : 'Verificar'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <button
              onClick={handleResend}
              disabled={cooldown > 0 || resending}
              className="font-semibold text-brand-600 hover:underline disabled:opacity-50 disabled:no-underline"
            >
              {cooldown > 0
                ? `Reenviar en ${cooldown}s`
                : resending
                ? 'Reenviando...'
                : 'Reenviar código'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}